import { Row, Col, Badge } from 'react-bootstrap'
import { GridCellEnum, ICustomColumn, IDealerChangedEvent, IGridLocalStorageNames, IPageProps, IRootState } from 'types'
import { DefaultButton, FormDropDownList, FormSwitch, MessageAlert, PageHeader } from 'components'
import { useRef, useState } from 'react'
import { FormElement, Field, Form, FormClassComponent, } from '@progress/kendo-react-form'
import { RequiredValidator } from 'core/Validators'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import { V1ApiDealsClient } from 'core/http-common'
import {
    LS_MIGRATION_DEALS_PAGE_SIZE,
    LS_MIGRATION_DEALS_SEARCH,
    LS_MIGRATION_DEALS_SELECTED_PAGE,
    LS_MIGRATION_DEALS_SORT,
    LS_MIGRATION_QUEUE_HISTORY_PAGE_SIZE,
    LS_MIGRATION_QUEUE_HISTORY_SEARCH,
    LS_MIGRATION_QUEUE_HISTORY_SELECTED_PAGE,
    LS_MIGRATION_QUEUE_HISTORY_SORT
} from 'core/consts'
import { BulkIdsDto, DealStatusEnum, DeletedStateEnum, F4MigrationDealerDealDto, F4MigrationStatusEnum, F4MigrationQueueResultDto, F4MigrationQueueResultStatusEnum, QueueDealMigrationRequestDto } from '@v1DealsApi'
import toast from 'core/toast'
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GridCellProps } from '@progress/kendo-react-grid'
import ConfirmModal from 'components/Modals/ConfirmModal'
import { hasStatusCode } from 'core/helpers'
import { ToastOptions } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import DealerSelect from 'components/Dealers/DealerSelect'
import { getFromLocalStorage, saveToLocalStorage } from 'core/local-storage-helpers'
import { AppDispatch } from 'state/store'

type MigrationStatusType = {
    text: string;
    value: F4MigrationStatusEnum;
}

const LOCAL_STORAGE_KEYS = {
    DEALER: 'migrationToolDealer'
}

const MigrationToolPage = ({ title }: IPageProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const formRef = useRef<FormClassComponent>(null)
    const gridRef = useRef<ISAGridRef>(null)
    const queueAllFormRef = useRef<FormClassComponent>(null)
    const queueSelectedFormRef = useRef<FormClassComponent>(null)

    const loading = useSelector((state: IRootState) => state.app.loading)

    const migrationTypeData = ['Deals']
    const [migrationType, setMigrationType] = useState<string>(migrationTypeData[0])

    const migrationStatusData: Array<MigrationStatusType> = [
        { text: 'All', value: F4MigrationStatusEnum.All },
        { text: 'Not Migrated', value: F4MigrationStatusEnum.NotMigrated },
        { text: 'Queued', value: F4MigrationStatusEnum.Queued },
        { text: 'Failed', value: F4MigrationStatusEnum.Failed },
        { text: 'Partial Success', value: F4MigrationStatusEnum.PartialSuccess },
        { text: 'Success', value: F4MigrationStatusEnum.Success },
        { text: 'Ineligible', value: F4MigrationStatusEnum.Ineligible },
    ]

    const [migrationStatus, setMigrationStatus] = useState<MigrationStatusType>(migrationStatusData[0])

    // The API shows every closed deal plus in-progress deals modified on/after Jan 1 of the current
    // year (see F4MigrationDtoService.GetShowableDeals). Surface that scope so CS understands the count.
    const inProgressSinceLabel = `January 1, ${new Date().getFullYear()}`

    const [showQueueHistoryModal, setShowQueueHistoryModal] = useState<boolean>(false)
    const [showQueueAllConfirm, setShowQueueAllConfirm] = useState<boolean>(false)
    const [showQueueSelectedConfirm, setShowQueueSelectedConfirm] = useState<boolean>(false)
    const [pendingQueueIds, setPendingQueueIds] = useState<number[]>([])
    const queueHistoryGridRef = useRef<ISAGridRef>(null)
    const [queueHistoryDeal, setQueueHistoryDeal] = useState<F4MigrationDealerDealDto | undefined>(undefined)
    const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(getFromLocalStorage(LOCAL_STORAGE_KEYS.DEALER) as number | undefined)

    const getBadgeVariant = (statusType: F4MigrationQueueResultStatusEnum | undefined) => {
        switch (statusType) {
            case F4MigrationQueueResultStatusEnum.Failed:
                return 'danger'
            case F4MigrationQueueResultStatusEnum.Success:
                return 'success'
            case F4MigrationQueueResultStatusEnum.PartialSuccess:
                return 'warning'
            default:
                return 'secondary'
        }
    }

    const HistoryStatusCell = (props: GridCellProps) => {
        const item = props.dataItem as F4MigrationQueueResultDto
        return (
            <td className='text-center'>
                {item.f4migrationQueueResultStatusId ?
                    <Badge bg={getBadgeVariant(item.f4migrationQueueResultStatusId)}>
                        {item.f4migrationQueueResultStatusName}
                    </Badge>
                    : null
                }
            </td>
        )
    }

    const HistoryViewOldDealInDcsCell = (props: GridCellProps) => {
        let item = props.dataItem as F4MigrationQueueResultDto

        const url = item.dealStatusId === DealStatusEnum.Closed
            ? `${process.env.REACT_APP_OLD_DCS_URL}/MyAccount/CRM/Deals/EditDeal.aspx?dealid=${item.refId}`
            : `${process.env.REACT_APP_OLD_DCS_URL}/MyAccount/CRM/Worksheets/EditWorksheet.aspx?worksheetid=${item.refId}`


        return (
            <td
                className='text-center'
                title={'View old deal in DCS'}
                onClick={() => { window.open(url, '_blank')?.focus() }}>
                <a
                    className='btn btn-default btn-sm gridButton'
                    role='button'
                >
                    <FontAwesomeIcon icon={['fas', 'up-right-from-square']} />
                </a>
            </td>
        )
    }

    const HistoryViewNewDealInDcsCell = (props: GridCellProps) => {
        let item = props.dataItem as F4MigrationQueueResultDto

        const isDisabled = !item || !item.f4saleId

        const url = item.dealStatusId === DealStatusEnum.Closed
            ? `${process.env.REACT_APP_DCS_ADMIN_V2_URL}/deals/edit/${item.f4saleId}`
            : `${process.env.REACT_APP_DCS_ADMIN_V2_URL}/deals/worksheets/edit/${item.f4saleId}`

        return (
            <td
                className='text-center'
                title={'View new deal in DCS'}
                onClick={isDisabled ? undefined : () => { window.open(url, '_blank')?.focus() }}>
                <a
                    className='btn btn-default btn-sm gridButton'
                    role='button'
                    style={isDisabled ? { opacity: 0.5 } : undefined}
                >
                    <FontAwesomeIcon icon={['fas', 'up-right-from-square']} />
                </a>
            </td>
        )
    }

    const ViewQueueHistoryCell = (props: GridCellProps) => {
        let item = props.dataItem as F4MigrationDealerDealDto
        const isDisabled = !item.f4migrationQueueResultCount || item.f4migrationQueueResultCount < 1

        return (
            <td
                className='text-center'
                title={isDisabled ? 'No queue history available' : 'View queue history'}
                onClick={isDisabled ? undefined : () => {
                    setQueueHistoryDeal(item)
                    setShowQueueHistoryModal(true)
                }}>

                <a
                    className='btn btn-default btn-sm gridButton'
                    role='button'
                    style={isDisabled ? { opacity: 0.5 } : undefined}
                >
                    <FontAwesomeIcon icon={['fas', 'history']} />
                </a>

            </td>
        )
    }

    const queueHistoryColumns: ICustomColumn[] = [
        { field: 'f4migrationQueueResultId', title: 'ID', width: '100px' },
        { field: 'f4migrationQueueResultStatusName', title: 'Status', sortable: false, width: '100px', cell: HistoryStatusCell },
        { field: 'f4saleId', title: 'Sale ID', width: '110px' },
        { field: 'message', title: 'Message' },
        { field: '', title: 'Old Deal', width: '120px', cell: HistoryViewOldDealInDcsCell, sortable: false },
        { field: '', title: 'New Deal', width: '120px', cell: HistoryViewNewDealInDcsCell, sortable: false },
        { field: 'processedDate', title: 'Processed Date', format: '{0:g}', filterType: 'date', width: '180px' },
    ]


    const getDealQueueStatus = (item: F4MigrationDealerDealDto) => {
        switch (item.latestF4migrationQueueResultStatusId) {
            case F4MigrationQueueResultStatusEnum.Failed:
                return 'Failed'
            case F4MigrationQueueResultStatusEnum.Success:
                return 'Success'
            case F4MigrationQueueResultStatusEnum.PartialSuccess:
                return 'Partial Success'
            default:
                if (item.f4migrationQueueCount > 0 && item.f4migrationQueueResultCount <= 0)
                    return 'Queued'
                // No queue history and blocked by a data problem -> won't be migrated.
                if (item.isMigratable === false)
                    return 'Ineligible'
                return 'Not Migrated'
        }
    }

    const DealQueueStatusCell = (props: GridCellProps) => {
        const item = props.dataItem as F4MigrationDealerDealDto
        const status = getDealQueueStatus(item)
        // Ineligible isn't a queue-result status, so it gets its own (warning) badge; everything else
        // maps from the latest result status.
        const variant = status === 'Ineligible'
            ? 'warning'
            : getBadgeVariant(item.latestF4migrationQueueResultStatusId)
        return (
            <td className='text-center'>
                <Badge bg={variant}>
                    {status}
                </Badge>
            </td>
        )
    }

    const DealViewOldDealInDcsCell = (props: GridCellProps) => {
        let item = props.dataItem as F4MigrationDealerDealDto

        const url = item.dealStatusId === DealStatusEnum.Closed
            ? `${process.env.REACT_APP_OLD_DCS_URL}/MyAccount/CRM/Deals/EditDeal.aspx?dealid=${item.dealerDealId}`
            : `${process.env.REACT_APP_OLD_DCS_URL}/MyAccount/CRM/Worksheets/EditWorksheet.aspx?worksheetid=${item.dealerDealId}`


        return (
            <td
                className='text-center'
                title={'View old deal in DCS'}
                onClick={() => { window.open(url, '_blank')?.focus() }}>
                <a
                    className='btn btn-default btn-sm gridButton'
                    role='button'
                >
                    <FontAwesomeIcon icon={['fas', 'up-right-from-square']} />
                </a>
            </td>
        )
    }

    const DealViewNewDealInDcsCell = (props: GridCellProps) => {
        let item = props.dataItem as F4MigrationDealerDealDto

        const isDisabled = !item || !item.latestF4migrationQueueResultSaleId

        const url = item.dealStatusId === DealStatusEnum.Closed
            ? `${process.env.REACT_APP_DCS_ADMIN_V2_URL}/deals/edit/${item.latestF4migrationQueueResultSaleId}`
            : `${process.env.REACT_APP_DCS_ADMIN_V2_URL}/deals/worksheets/edit/${item.latestF4migrationQueueResultSaleId}`

        return (
            <td
                className='text-center'
                title={isDisabled ? 'Not migrated to a new deal' : 'View new deal in DCS'}
                onClick={isDisabled ? undefined : () => { window.open(url, '_blank')?.focus() }}>
                <a
                    className='btn btn-default btn-sm gridButton'
                    role='button'
                    style={isDisabled ? { opacity: 0.5 } : undefined}
                >
                    <FontAwesomeIcon icon={['fas', 'up-right-from-square']} />
                </a>
            </td>
        )
    }

    const MESSAGE_TRUNCATE_LENGTH = 120

    const DealDetailsCell = (props: GridCellProps) => {
        const item = props.dataItem as F4MigrationDealerDealDto
        const isError = item.latestF4migrationQueueResultStatusId === F4MigrationQueueResultStatusEnum.Failed
            || item.latestF4migrationQueueResultStatusId === F4MigrationQueueResultStatusEnum.PartialSuccess

        const message = item.latestF4migrationQueueResultMessage
        const truncatedMessage = message && message.length > MESSAGE_TRUNCATE_LENGTH
            ? `${message.substring(0, MESSAGE_TRUNCATE_LENGTH)}…`
            : message

        // Pre-queue "won't migrate" reasons (missing buyer name/address, no vehicle, incomplete
        // co-buyer). These deals are shown so CS can see them, but are skipped at queue time.
        const blockedReasons = item.migrationBlockedReasons ?? []

        return (
            <td>
                <div>{item.vehicleTitle}</div>
                {item.vehicleStock
                    ? <div className='text-muted small'>Stock: {item.vehicleStock}</div>
                    : null}
                {blockedReasons.length > 0
                    ? <div className='small text-danger' title={blockedReasons.join('; ')}>
                        <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} className='me-1' />
                        Won't migrate: {blockedReasons.join('; ')}
                    </div>
                    : null}
                {message
                    ? <div className={`small ${isError ? 'text-danger' : 'text-muted'}`} title={message}>
                        {truncatedMessage}
                    </div>
                    : null}
            </td>
        )
    }

    const columns: ICustomColumn[] = [
        { field: 'dealerDealId', title: 'Deal ID', width: '110px' },
        { field: 'customerBuyerFullName', title: 'Name', width: '140px' },
        { field: 'vehicleTitle', title: 'Vehicle / Message', cell: DealDetailsCell, sortable: false },
        { field: 'dealStatusName', title: 'Deal Status', width: '140px' },
        { field: 'dealClosedDate', title: 'Closed Date', format: '{0:d}', filterType: 'date', width: '150px' },
        { field: 'latestF4migrationQueueDate', title: 'Last Queued', format: '{0:g}', filterType: 'date', width: '170px' },
        { field: 'latestF4MigrationQueueResultStatusId', title: 'Migrated', width: '120px', cell: DealQueueStatusCell },
        { field: '', title: 'Old Deal', width: '100px', cell: DealViewOldDealInDcsCell, sortable: false },
        { field: '', title: 'New Deal', width: '100px', cell: DealViewNewDealInDcsCell, sortable: false },
        { field: '', title: 'History', cell: ViewQueueHistoryCell, sortable: false, width: '80px' },
        { field: 'dealerDealId', title: 'Select', sortable: false, headerClassName: 'text-center', width: '60px', cellType: GridCellEnum.MultiSelect },
    ]

    const defaultAction = { text: 'Action...', id: 0 }

    const actions = [
        { text: 'Action...', id: 0 },
        { text: 'Queue', id: 1 }
    ]

    const dealsGridLocalStorageNames: IGridLocalStorageNames =
    {
        pageSize: LS_MIGRATION_DEALS_PAGE_SIZE,
        search: LS_MIGRATION_DEALS_SEARCH,
        selectedPage: LS_MIGRATION_DEALS_SELECTED_PAGE,
        sort: LS_MIGRATION_DEALS_SORT
    }

    const queueHistoryGridLocalStorageNames: IGridLocalStorageNames =
    {
        pageSize: LS_MIGRATION_QUEUE_HISTORY_PAGE_SIZE,
        search: LS_MIGRATION_QUEUE_HISTORY_SEARCH,
        selectedPage: LS_MIGRATION_QUEUE_HISTORY_SELECTED_PAGE,
        sort: LS_MIGRATION_QUEUE_HISTORY_SORT
    }

    const getDealsData = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        if (!selectedDealerId || !migrationType || !migrationStatus) {
            return {
                data: [],
                total: 0
            }
        }

        try {

            const result = await V1ApiDealsClient.getMigrationDealsByDealerId(
                selectedDealerId,
                DeletedStateEnum.Active,
                migrationStatus?.value,
                props.search,
                props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                props.paging ? props.paging.take : 10,
                true,
                props.sortBy,
                props.sortAscending,
            )

            return {
                data: result.data,
                total: result.totalItems ? result.totalItems : 0
            }
        }
        catch (e) {
            toast.responseErrors(e)
        }
        return {
            data: [],
            total: 0
        }
    }

    const getQueueHistoryData = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        if (!queueHistoryDeal) {
            return {
                data: [],
                total: 0
            }
        }

        try {

            const result = await V1ApiDealsClient.getF4migrationQueueResults(
                queueHistoryDeal?.dealerDealId,
                props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                props.paging ? props.paging.take : 10,
                true,
                props.sortBy,
                props.sortAscending,
            )

            return {
                data: result.data,
                total: result.totalItems ? result.totalItems : 0
            }
        }
        catch (e) {
            toast.responseErrors(e)
        }
        return {
            data: [],
            total: 0
        }
    }

    const handleDealerClear = async () => {
        setSelectedDealerId(undefined)
        saveToLocalStorage(LOCAL_STORAGE_KEYS.DEALER, undefined)
        gridRef.current?.resetAndFetch()
    }

    const handleDealerChange = async (e: IDealerChangedEvent) => {
        setSelectedDealerId(e.dealerId)
        saveToLocalStorage(LOCAL_STORAGE_KEYS.DEALER, e.dealerId)
        gridRef.current?.resetAndFetch()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onActionChange = async (event: DropDownListChangeEvent, ids: number[], selectedData: any[] | undefined) => {
        if (ids.length === 0) {
            toast.warn('No Items Selected')
            return
        }

        if (!selectedDealerId)
            return

        switch (event.value.id) {
            case 1: {
                // Confirm (with the Force Resync toggle) before queuing the selected deals.
                setPendingQueueIds(ids)
                setShowQueueSelectedConfirm(true)
                break
            }
        }
    }

    const handleQueueSelected = async () => {
        setShowQueueSelectedConfirm(false)

        if (!selectedDealerId || pendingQueueIds.length === 0)
            return

        const forceResync = queueSelectedFormRef.current?.valueGetter('forceResync') === true

        try {
            await dispatch(startLoadingAction())

            const response = await V1ApiDealsClient.queueDealMigrations(
                selectedDealerId,
                new QueueDealMigrationRequestDto({ bulkIds: new BulkIdsDto({ ids: pendingQueueIds }), forceResync })
            )
            const skipped = response.responses ? response.responses.filter(r => hasStatusCode(r.statusCode, 409)) : []
            const errrors = response.responses ? response.responses.filter(r => !hasStatusCode(r.statusCode, 200) && !hasStatusCode(r.statusCode, 409)) : []
            const successes = response.responses ? response.responses.filter(r => hasStatusCode(r.statusCode, 200)) : []

            toast.info(
                <>
                    <span>Queued Summary:</span>
                    <br />
                    <ul>
                        <li>Skipped: {skipped.length}</li>
                        <li>Error: {errrors.length}</li>
                        <li>Queued: {successes.length}</li>
                    </ul>
                </>,
                {
                    autoClose: 10000,
                } as ToastOptions
            )

            setTimeout(() => gridRef.current?.fetch())
        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            setPendingQueueIds([])
            await dispatch(endLoadingAction())
        }
    }

    const handleQueueAll = async () => {
        setShowQueueAllConfirm(false)

        if (!selectedDealerId)
            return

        const forceResync = queueAllFormRef.current?.valueGetter('forceResync') === true

        try {
            await dispatch(startLoadingAction())

            const result = await V1ApiDealsClient.queueAllDealMigrationsToF4Sales(selectedDealerId, DeletedStateEnum.Active, forceResync)

            toast.info(
                <>
                    <span>Queue All Summary:</span>
                    <br />
                    <ul>
                        <li>Eligible: {result.eligible}</li>
                        <li>Queued: {result.queued}</li>
                        <li>Skipped (already migrated): {result.skippedAlreadyMigrated}</li>
                        <li>Skipped (pending): {result.skippedPending}</li>
                        <li>Skipped (won't migrate): {result.skippedIneligible}</li>
                    </ul>
                </>,
                {
                    autoClose: 10000,
                } as ToastOptions
            )

            setTimeout(() => gridRef.current?.fetch())
        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    return (
        <div id='MigrationToolPage'>
            <PageHeader title={title}>
                <DefaultButton
                    title='Queue all eligible deals for this dealer'
                    className='me-2'
                    onClick={() => setShowQueueAllConfirm(true)}
                    disabled={!selectedDealerId || !migrationType || !migrationStatus || loading}
                    loading={loading}
                    spinnerVariant='default'
                    variant='primary'>
                    <FontAwesomeIcon icon={['fas', 'layer-group']} /> Queue All
                </DefaultButton>
                <DefaultButton
                    title='Refresh deal migration data'
                    onClick={() => { gridRef.current?.fetch() }}
                    disabled={!selectedDealerId || !migrationType || !migrationStatus || loading}
                    loading={loading}
                    spinnerVariant='default'
                    variant='default'>
                    <FontAwesomeIcon icon={['fas', 'sync']} /> Refresh
                </DefaultButton>
            </PageHeader>

            <Form
                ref={formRef}
                initialValues={{
                    migrationType: migrationTypeData[0],
                    migrationStatus: migrationStatusData[0],
                    dealer: null
                }}
                ignoreModified={true}
                render={() => (
                    <FormElement>
                        <Row className='mb-3'>
                            <Col md={4}>
                                <Field
                                    label='Migration Type:'
                                    name='migrationType'
                                    validator={RequiredValidator}
                                    required
                                    component={FormDropDownList}
                                    data={migrationTypeData}
                                    onChange={() => {
                                        setMigrationType(formRef.current?.valueGetter('migrationType') || '')
                                        gridRef.current?.resetAndFetch()
                                    }}
                                />
                            </Col>
                            <Col md={4}>
                                <Field
                                    label='Migration Status:'
                                    name='migrationStatus'
                                    validator={RequiredValidator}
                                    required
                                    textField='text'
                                    dataItemKey='value'
                                    component={FormDropDownList}
                                    data={migrationStatusData}
                                    onChange={() => {
                                        setMigrationStatus(formRef.current?.valueGetter('migrationStatus') || '')
                                        gridRef.current?.resetAndFetch()
                                    }}
                                />
                            </Col>
                            <Col md={4} className='mb-3 mb-md-0'>
                                <Field
                                    name='dealer'
                                    label='Dealer:'
                                    component={DealerSelect}
                                    dealerId={selectedDealerId ?? 0}
                                    disabled={loading}
                                    onDealerChanged={handleDealerChange}
                                    onDealerCleared={handleDealerClear}
                                    required={false}
                                />
                            </Col>
                        </Row>
                    </FormElement>
                )}
            />

            <hr />

            {!selectedDealerId || !migrationType || !migrationStatus
                ? <MessageAlert
                    alert={{
                        variant: 'info', messages: ['Please select a dealer.']
                    }}
                />
                : <>
                    <Row>
                        <Col className='text-center'>
                            <MessageAlert
                                alert={{
                                    variant: 'info',
                                    messages: [
                                        `Showing all closed deals and only in-progress deals modified since ${inProgressSinceLabel}.`
                                    ]
                                }}
                            />
                        </Col>
                    </Row>
                    <SAGrid
                        ref={gridRef}
                        columns={columns}
                        defaultSort={[{ field: 'latestF4migrationQueueDate', dir: 'desc' }]}
                        getData={getDealsData}
                        localStorageNames={dealsGridLocalStorageNames}
                        identifierPropertyName='dealerDealId'
                        action={{
                            default: defaultAction,
                            items: actions,
                            handler: onActionChange,
                            sendSelectedData: true,
                            popupWidth: '425px'
                        }}
                        features={
                            {
                                pageable: true,
                                reorderColumns: true,
                                sortable: true,
                                simpleSearch: true
                            }
                        }
                    />
                </>
            }

            <ConfirmModal
                size='xl'
                show={showQueueHistoryModal}
                title='Queue History'
                showCancelButton={false}
                confirmButtonTitle='Close'
                onCancel={(e) => {
                    setShowQueueHistoryModal(false)
                    setQueueHistoryDeal(undefined)
                }}
                onSave={(e) => {
                    setShowQueueHistoryModal(false)
                    setQueueHistoryDeal(undefined)
                }}
                content={
                    <>
                        {queueHistoryDeal && (
                            <SAGrid
                                ref={queueHistoryGridRef}
                                columns={queueHistoryColumns}
                                defaultSort={[{ field: 'processedDate', dir: 'desc' }]}
                                getData={getQueueHistoryData}
                                localStorageNames={queueHistoryGridLocalStorageNames}
                                identifierPropertyName='id'
                                features={{
                                    pageable: true,
                                    reorderColumns: true,
                                    sortable: true,
                                    simpleSearch: false
                                }}
                            />
                        )}
                    </>
                }
            />

            <ConfirmModal
                show={showQueueAllConfirm}
                title='Queue All Deals'
                confirmButtonTitle='Queue All'
                onCancel={() => setShowQueueAllConfirm(false)}
                onSave={() => { handleQueueAll() }}
                content={
                    <>
                        <p>
                            Queue every eligible deal for this dealer for migration to F4 Sales? Deals
                            awaiting processing are skipped automatically. Already-migrated deals are
                            skipped unless <strong>Force Resync</strong> is on, in which case they are
                            re-queued and upserted into F4 Sales.
                        </p>
                        <Form
                            ref={queueAllFormRef}
                            initialValues={{
                                forceResync: false
                            }}
                            ignoreModified={true}
                            render={() => (
                                <FormElement>
                                    <Field
                                        name='forceResync'
                                        label='Force Resync:'
                                        component={FormSwitch}
                                        onLabel='Yes'
                                        offLabel='No'
                                    />
                                </FormElement>
                            )}
                        />
                    </>
                }
            />

            <ConfirmModal
                show={showQueueSelectedConfirm}
                title='Queue Selected Deals'
                confirmButtonTitle='Queue'
                onCancel={() => {
                    setShowQueueSelectedConfirm(false)
                    setPendingQueueIds([])
                }}
                onSave={() => { handleQueueSelected() }}
                content={
                    <>
                        <p>
                            Queue the <strong>{pendingQueueIds.length}</strong> selected deal(s) for
                            migration to F4 Sales? Deals awaiting processing are skipped automatically.
                            Already-migrated deals are skipped unless <strong>Force Resync</strong> is
                            on, in which case they are re-queued and upserted into F4 Sales.
                        </p>
                        <Form
                            ref={queueSelectedFormRef}
                            initialValues={{
                                forceResync: false
                            }}
                            ignoreModified={true}
                            render={() => (
                                <FormElement>
                                    <Field
                                        name='forceResync'
                                        label='Force Resync:'
                                        component={FormSwitch}
                                        onLabel='Yes'
                                        offLabel='No'
                                    />
                                </FormElement>
                            )}
                        />
                    </>
                }
            />
        </div>
    )
}
export default MigrationToolPage